<template>
  <div>
    <h2 class="text-lg font-semibold text-gray-900 mb-4">{{ t("title") }}</h2>
    <p class="text-lg pl-0.5 mt-1 tracking-tight text-gray-700">
      {{ totalSales?.current }}
      <span v-if="totalSales?.previous != null" class="text-sm ml-4 text-gray-500">
        {{ totalSales.previous }} {{ t("previous_period") }}
      </span>
      <Variation class="ml-2" :variation="totalSales?.variation" />
    </p>
    <Loading v-if="loading" />
    <div v-else-if="!hasContracts" class="text-center py-8 text-gray-500">
      <p>{{ t("no_results") }}</p>
    </div>
    <LineChart v-else-if="isDailyView" :data="dailyChartData" :options="chartOptions" :chart-height="200" />
    <LineChart v-else :data="monthlyChartData" :options="chartOptions" :chart-height="200" />
  </div>
</template>

<script>
import { mapState } from "vuex";
import { useI18n } from "vue-i18n";
import { LineChart, Loading, Variation } from "@estaly/ui";

export default {
  name: "DailyContractsChart",
  components: { LineChart, Loading, Variation },

  props: {
    loading: { type: Boolean, default: false },
    chartView: { type: String, required: true },
  },

  setup() {
    const { t } = useI18n();
    return { t };
  },

  computed: {
    ...mapState("analytics", { generalAnalytics: "generalAnalytics" }),

    isDailyView() {
      return this.chartView === "daily";
    },

    totalSales() {
      return this.generalAnalytics?.general?.periodContracts;
    },

    dailyChartData() {
      const daily = this.generalAnalytics?.sales?.dailyPlanSales;
      if (!daily?.current) return {};

      const currentData = daily.current.map((entry) => ({ date: entry.date, value: entry.count }));
      const chartData = { sold: { color: "#8C1EFC", value: currentData } };
      // Without a comparison period, no previous series: an empty or zero-filled line would be misleading.
      if (!daily.previous?.length) return chartData;

      const previousData = currentData.map((entry, index) => ({
        date: entry.date,
        value: daily.previous[index]?.count || 0,
      }));
      return { ...chartData, "previous contracts sold": { color: "#D1D5DB", value: previousData } };
    },

    monthlyChartData() {
      const monthly = this.generalAnalytics?.sales?.monthlyPlanSales;
      const currentData = monthly?.current?.map((entry) => ({ date: entry.date, value: entry.count })) || [];

      return {
        sold: { color: "#8C1EFC", value: currentData },
      };
    },

    activeChartData() {
      return this.isDailyView ? this.dailyChartData : this.monthlyChartData;
    },

    hasContracts() {
      const data = this.activeChartData;
      return (data?.sold?.value?.length || 0) > 0;
    },

    maxValue() {
      const datasets = Object.values(this.activeChartData);
      const allValues = datasets.flatMap((d) => (d.value || []).map((p) => p.value));
      return Math.max(1, ...allValues);
    },

    chartOptions() {
      const isDaily = this.isDailyView;
      const data = this.activeChartData;
      const maxVal = isDaily ? this.maxValue : this.maxValue * 1.15;

      return {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        scales: {
          x: {
            grid: { display: false },
            ticks: {
              maxRotation: 0,
              minRotation: 0,
              callback: (value, index, values) => {
                const step = isDaily ? 4 : 2;
                if (index === 0 || index === values.length - 1 || index % step === 0) {
                  return data["sold"]?.value[index]?.date || value;
                }
                return "";
              },
            },
          },
          y: {
            grid: { display: false, drawBorder: false },
            beginAtZero: true,
            ticks: {
              maxTicksLimit: 2,
              callback: (value) => {
                if (value === 0) return value;
                if (isDaily && value === this.maxValue) return value;
                if (!isDaily && Math.abs(value - this.maxValue * 1.15) < 1) return this.maxValue;
                return "";
              },
            },
            suggestedMax: maxVal,
            max: maxVal,
          },
        },
        plugins: {
          annotation: false,
          datalabels: false,
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (tooltipItem) => ` ${tooltipItem.dataset.label} : ${tooltipItem.raw.y.toLocaleString()}`,
            },
          },
        },
      };
    },
  },
};
</script>

<i18n src="./DailyContractsChart.i18n.json"></i18n>
